// Shared registration primitives for the Highcharts chart-family (Phase 3).
// Every HC question-view (bars, matrix, maxdiff, polarity, rapid-association, cf,
// histogram, compare-items…) renders through the same ChartContainer, whose one-time
// hook attachment (Phase 2) already makes any HC mode exportable. These helpers remove
// the per-chart copy-paste: `commonHcProps` is the ~universal prop core, `registerHc`
// registers one live component under N legacy dispatch keys with one toProps.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/03-highcharts-rollout.md §4.1

import {
  ExportableCharts,
  type ExportableChart
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

// The prop core shared by the HC-family Chart components (verified against bars/mx/md/ra
// signatures — §4.0). Each component destructures only what it reads; unread props are
// ignored (do not spread {...props} onto a DOM node in any HC Chart, else these leak as
// attributes). Interactive callbacks stay undefined in headless.
export const commonHcProps = (data: any, params: any) => ({
  questionKey: data.questionKey,
  qType: data.qType,
  chartMode: params.chartMode,
  globalChartMode: params.globalChartMode,
  answer: data.answer,
  batches: data.batches ?? null,
  sortOrder: params.sortOrder || 'iasc',
  format: data.format || {},
  colorTheme: data.colorTheme,
  chartLegendMode: params.chartLegendMode,
  features: data.features || {},
  isTraitType: data.isTraitType ?? false,
  // No-op (not undefined): some HC components declare onChartModeChange as required —
  // a no-op satisfies PropTypes and never fires in headless (no interaction).
  onChartModeChange: () => {}
})

// Register ONE Component under N legacy PPTX dispatch keys with ONE toProps.
// e.g. registerHc(['mx','sr','se','ps','mp','ro','aro'], MatrixChart, mxToProps)
export const registerHc = (
  keys: string[],
  Component: ExportableChart['Component'],
  toProps: ExportableChart['toProps']
): void => {
  keys.forEach(key => ExportableCharts.register(key, { Component, toProps }))
}
